import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

/* Poolcorp Custom Code */
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import {
    AccordionSectionPanel,
    AccordionSectionPresentationProps,
    ManagedAccordionSection,
} from "@insite/mobius/AccordionSection";
import Hidden from "@insite/mobius/Hidden";
import { StyledClickable, StyledTypography } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { contentSpacingStyles, gridGapStyles, siteWidthStyles } from "@store/theme/common/mixins";
import { TYPOGRAPHY } from "@store/theme/design";
import { mediaOptions } from "@store/utilities/breakpointMediaQueries";
/* Poolcorp Custom Code */

const enum fields {
    firstTitle = "firstTitle",
    secondTitle = "secondTitle",
    thirdTitle = "thirdTitle",
    fourthTitle = "fourthTitle",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.firstTitle]: string;
        [fields.secondTitle]: string;
        [fields.thirdTitle]: string;
        [fields.fourthTitle]: string;
    };
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
    // Poolcorp Custom Code
    linkListGridItem?: GridItemProps;
    accordion: AccordionPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
    firstColumnGridItem?: GridItemProps;
    firstColumnStyledWrapper?: InjectableCss;
    linkListColumnsGridItem?: GridItemProps;
    widgetStyledWrapper?: InjectableCss;
    widgetStyledContainer?: InjectableCss;
    footerLegalStyledWrapper?: InjectableCss;
    footerLegalStyledContainer?: InjectableCss;
    footerLegalGridContainer?: GridContainerProps;
    footerLegalGridItem?: GridItemProps;
    footerLinksListGridItem?: GridItemProps;
    headerStyles?: TypographyPresentationProps;
    linkMenuItemsWrapper?: InjectableCss;
}

const footerLegalGridItemProps: GridItemProps = {
    width: [12, 12, 6, 6, 6],
    align: "middle",
    css: css`
        ${props =>
            breakpointMediaQueries(
                props.theme,
                [
                    null,
                    css`
                        justify-content: center;
                    `,
                ],
                mediaOptions.max,
            )}
    `,
};

export const footerContainerStyles: FooterContainerStyles = {
    firstColumnGridItem: {
        width: [12, 12, 12, 5, 5],
    },
    linkListColumnsGridItem: {
        width: [12, 12, 12, 7, 7],
        css: css`
            display: block;
        `,
    },
    linkListGridItem: {
        width: [12, 4, 4, 4, 4],
        css: css`
            flex-direction: column;
        `,
    },
    accordion: {
        css: css`
            border-bottom: 1px solid ${getColor("footer.bottom")};
        `,
    },
    accordionSection: {
        titleTypographyProps: {
            weight: TYPOGRAPHY.weight.black,
            size: TYPOGRAPHY.size.base,
        },
        headerProps: {
            css: css`
                background-color: transparent;

                button {
                    background-color: inherit;
                    padding: 1em 0;
                    border: 0;
                    border-top: 1px solid ${getColor("footer.bottom")};
                    border-bottom: 0;
                    font-weight: ${TYPOGRAPHY.weight.black};
                    font-size: ${TYPOGRAPHY.size.base};
                    color: ${getColor("footer.contrastMain")};

                    &:focus {
                        padding: 1em 0;
                        border: 0;
                        border-top: 1px solid ${getColor("footer.bottom")};
                        outline-color: ${getProp("theme.focus.color")};
                        outline-style: ${getProp("theme.focus.style")};
                        outline-width: ${getProp("theme.focus.width")};
                        outline-offset: -${getProp("theme.focus.width")};
                    }

                    &:hover {
                        text-decoration: underline;
                    }
                }

                ${/* sc-selector */ AccordionSectionPanel} + && {
                    margin-top: -1px;

                    button {
                        border-top: 1px solid ${getColor("footer.bottom")};

                        &:focus {
                            padding: 1em 0;
                            border: 0;
                            border-top: 1px solid ${getColor("footer.bottom")};
                        }
                    }
                }
            `,
        },
        panelProps: {
            css: css`
                padding: 0 0 1em;
                border: 0;
            `,
        },
    },
    widgetStyledWrapper: {
        css: css`
            background-color: ${getColor("footer")};
            color: ${getColor("footer.contrastMain")};
            padding: ${TYPOGRAPHY.spacing.larger} 0;
            font-size: ${TYPOGRAPHY.size.small};
            ${props =>
                breakpointMediaQueries(
                    props.theme,
                    [
                        null,
                        css`
                            padding: ${TYPOGRAPHY.spacing.largest} 0;
                        `,
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    widgetStyledContainer: {
        css: css`
            ${siteWidthStyles()}
        `,
    },
    widgetGridContainer: {
        gap: 0,
        css: css`
            ${gridGapStyles(["30px"], [TYPOGRAPHY.spacing.larger])}
        `,
    },
    firstColumnStyledWrapper: {
        css: css`
            max-width: 400px;
        `,
    },
    footerLegalStyledWrapper: {
        css: css`
            background-color: ${getColor("footer.bottom")};
            font-size: ${TYPOGRAPHY.size.tiny};
            line-height: ${TYPOGRAPHY.lineHeight.small};
            padding: ${TYPOGRAPHY.spacing.base} 0;
        `,
    },
    footerLegalStyledContainer: {
        css: css`
            ${siteWidthStyles()}
        `,
    },
    footerLegalGridContainer: { gap: 4 },
    footerLegalGridItem: {
        ...footerLegalGridItemProps,
        css: css`
            ${footerLegalGridItemProps.css}

            > div {
                display: flex;
                align-items: center;
                width: auto;

                > * {
                    margin-right: 1em;

                    &:last-child {
                        margin-right: 0;
                    }
                }
            }
        `,
    },
    footerLinksListGridItem: {
        ...footerLegalGridItemProps,
        css: css`
            ${footerLegalGridItemProps.css}
            justify-content: flex-end;
        `,
    },
    headerStyles: {
        forwardAs: "h6",
        css: css`
            font-weight: ${TYPOGRAPHY.weight.black};
            margin-bottom: ${TYPOGRAPHY.spacing.base};
            line-height: ${TYPOGRAPHY.lineHeight.small};
            font-size: ${TYPOGRAPHY.size.base};
        `,
    },
    linkMenuItemsWrapper: {
        css: css`
            p {
                ${contentSpacingStyles(null, TYPOGRAPHY.spacing.small)}
            }

            ${StyledClickable} {
                ${contentSpacingStyles(null, TYPOGRAPHY.spacing.small)}
                color: inherit;

                > ${StyledTypography} {
                    color: inherit;
                    font-weight: ${TYPOGRAPHY.weight.base};
                    text-transform: initial;
                }

                &:hover {
                    text-decoration: underline;

                    > ${StyledTypography} {
                        color: inherit;
                    }
                }
            }
        `,
    },
    /* Poolcorp Custom Code */
};

const FooterContainer: FC<OwnProps> = ({ fields, id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    return (
        <>
            {/* Poolcorp Custom Code */}
            <StyledWrapper {...styles.widgetStyledWrapper}>
                <StyledWrapper {...styles.widgetStyledContainer}>
                    <GridContainer {...styles.widgetGridContainer}>
                        <GridItem {...styles.firstColumnGridItem}>
                            <StyledWrapper {...styles.firstColumnStyledWrapper}>
                                <Typography {...styles.headerStyles}>{fields.firstTitle}</Typography>
                                <StyledWrapper>
                                    <Zone zoneName="FirstColumn" contentId={id} />
                                </StyledWrapper>
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.linkListColumnsGridItem}>
                            <Hidden above="xs">
                                <Accordion headingLevel={2} {...styles.accordion}>
                                    <ManagedAccordionSection title={fields.secondTitle} {...styles.accordionSection}>
                                        <StyledWrapper {...styles.linkMenuItemsWrapper}>
                                            <Zone zoneName="LinkList1" contentId={id} />
                                        </StyledWrapper>
                                    </ManagedAccordionSection>
                                    <ManagedAccordionSection title={fields.thirdTitle} {...styles.accordionSection}>
                                        <StyledWrapper {...styles.linkMenuItemsWrapper}>
                                            <Zone zoneName="LinkList2" contentId={id} />
                                        </StyledWrapper>
                                    </ManagedAccordionSection>
                                    <ManagedAccordionSection title={fields.fourthTitle} {...styles.accordionSection}>
                                        <StyledWrapper {...styles.linkMenuItemsWrapper}>
                                            <Zone zoneName="LinkList3" contentId={id} />
                                        </StyledWrapper>
                                    </ManagedAccordionSection>
                                </Accordion>
                            </Hidden>
                            <Hidden below="sm">
                                <GridContainer>
                                    <GridItem {...styles.linkListGridItem}>
                                        <Typography {...styles.headerStyles}>{fields.secondTitle}</Typography>
                                        <StyledWrapper {...styles.linkMenuItemsWrapper}>
                                            <Zone zoneName="LinkList1" contentId={id} />
                                        </StyledWrapper>
                                    </GridItem>
                                    <GridItem {...styles.linkListGridItem}>
                                        <Typography {...styles.headerStyles}>{fields.thirdTitle}</Typography>
                                        <StyledWrapper {...styles.linkMenuItemsWrapper}>
                                            <Zone zoneName="LinkList2" contentId={id} />
                                        </StyledWrapper>
                                    </GridItem>
                                    <GridItem {...styles.linkListGridItem}>
                                        <Typography {...styles.headerStyles}>{fields.fourthTitle}</Typography>
                                        <StyledWrapper {...styles.linkMenuItemsWrapper}>
                                            <Zone zoneName="LinkList3" contentId={id} />
                                        </StyledWrapper>
                                    </GridItem>
                                </GridContainer>
                            </Hidden>
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.footerLegalStyledWrapper}>
                <StyledWrapper {...styles.footerLegalStyledContainer}>
                    <GridContainer {...styles.footerLegalGridContainer}>
                        <GridItem {...styles.footerLegalGridItem}>
                            <Zone zoneName="FooterLegalZone" contentId={id} />
                        </GridItem>
                        <GridItem {...styles.footerLinksListGridItem}>
                            <StyledWrapper {...styles.linkMenuItemsWrapper}>
                                <Zone zoneName="FooterLegalLinks" contentId={id} />
                            </StyledWrapper>
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>
            </StyledWrapper>
            {/* Poolcorp Custom Code */}
        </>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
        /* Poolcorp Custom Code */
        fieldDefinitions: [
            {
                name: fields.firstTitle,
                displayName: "First Column Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.secondTitle,
                displayName: "Second Column Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.thirdTitle,
                displayName: "Third Column Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.fourthTitle,
                displayName: "Fourth Column Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
        ],
        /* Poolcorp Custom Code */
    },
};

export default footerContainer;
